import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Upload } from 'lucide-react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { LinkedRecordBar, PageHeader } from '@/components/core';
import { BulkSelectionBar } from '@/components/core/bulk-selection-bar/bulk-selection-bar';
import {
  QueueStatusFilter,
  useQueueStatus,
} from '@/components/core/queue-status-filter/queue-status-filter';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord, useUrlTab } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import { fetchAllPages } from '@/lib/fetch-all-pages';
import { mergeWhere } from '@/lib/table-query';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useDepartmentScope, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { ManageAttendanceFilters } from '../../components/manage-attendance-filters/manage-attendance-filters';
import { Button } from '@/components/ui-kit/button';
import { Label } from '@/components/ui-kit/label';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  canTakeLunch,
  getOrgDateString,
} from '@/modules/time-attendance/attendance/services/attendance-rules';
import { useAttendancePolicy } from '@/modules/time-attendance/attendance/hooks/use-attendance-policy';
import {
  createAttendanceTableColumns,
  createRosterTableColumns,
} from '../../components/attendance-table-columns/attendance-table-columns';
import {
  createCorrectionRequestsTableColumns,
  isReviewable,
} from '../../components/correction-requests-table/correction-requests-table-columns';
import { BulkReviewDialog } from '../../components/bulk-review-dialog/bulk-review-dialog';
import { AttendanceExportButtons } from '../../components/attendance-export/attendance-export-buttons';
import { ImportAttendanceDialog } from '../../components/import-attendance-dialog/import-attendance-dialog';
import {
  CORRECTION_QUEUES,
  CorrectionQueue,
  correctionQueueFilter,
  useCorrectionQueueCounts,
  useCurrentReviewer,
  useGetAttendanceCorrections,
  useGetAttendanceRoster,
  useGetAttendances,
  useReviewAttendanceCorrection,
  useSearchCorrectionEmployees,
  useWaiveCorrectionFine,
} from '../../hooks/use-manage-attendance';
import {
  DEFAULT_ATTENDANCE_CORRECTIONS_ORDER,
  DEFAULT_ATTENDANCES_ORDER,
  DEFAULT_ROSTER_ORDER,
} from '../../graphql/queries';
import { getAttendanceRoster, getAttendances } from '../../services/manage-attendance.service';
import {
  buildCorrectionFilter,
  buildManageAttendanceFilter,
  containsFilter,
} from '../../utils/manage-attendance-filter';
import {
  downloadAttendanceCsv,
  downloadAttendancePdf,
  exportRowsOfRecords,
  exportRowsOfRoster,
} from '../../utils/attendance-export';
import {
  AttendanceCorrection,
  AttendanceCorrectionsPagingResult,
  AttendanceRecord,
  AttendancesPagingResult,
  RosterRow,
  EMPTY_MANAGE_ATTENDANCE_FILTERS,
  ManageAttendanceFilters as ManageAttendanceFiltersValue,
} from '../../types/manage-attendance.types';
import {
  appPath,
  EMPLOYEE_ATTENDANCE_TABS,
  TEAM_ATTENDANCE_TABS,
  TeamAttendanceTab,
} from '@/constant/app-paths';
import { OvertimeList } from '@/modules/time-attendance/overtime';

/** An export reads the list page by page, bounded so a huge filter can't hang the browser. */
const EXPORT_PAGE_SIZE = 200;
const EXPORT_MAX_ROWS = 5000;

/**
 * With a day picked (today by default) the tab lists every employee in scope for that day, with or
 * without a record (the legacy view); with no day it lists the records of every day. Each view has
 * its own paging, sorting and column filters: the roster's sort and filter employees, the list's
 * records.
 */
const AttendanceTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canExport = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_EXPORT);
  const canImport = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_IMPORT);
  const [isImportOpen, setIsImportOpen] = useState(false);
  // The spot beside the day picker the cards' filter button is rendered into on a phone.
  const [filterSlot, setFilterSlot] = useState<HTMLSpanElement | null>(null);
  // Break time only for reviewers who can take breaks or lunch themselves.
  const policy = useAttendancePolicy();
  const showBreak = policy.canTakeBreak || canTakeLunch(policy);
  const today = getOrgDateString(new Date());
  const listTable = useServerTable({ pageSize: 15, defaultOrder: DEFAULT_ATTENDANCES_ORDER });
  const rosterTable = useServerTable({ pageSize: 15, defaultOrder: DEFAULT_ROSTER_ORDER });

  const [filters, setFilters] = useState<ManageAttendanceFiltersValue>({
    ...EMPTY_MANAGE_ATTENDANCE_FILTERS,
    date: today,
  });
  const { departments, getDepartmentIdCondition } = useDepartmentScope();
  const handleFiltersChange = (next: ManageAttendanceFiltersValue) => {
    setFilters(next);
    listTable.resetPage();
    rosterTable.resetPage();
  };

  const isRoster = Boolean(filters.date);
  const departmentCondition = getDepartmentIdCondition(filters.departmentId);
  const recordFilter = mergeWhere(
    buildManageAttendanceFilter(filters, departmentCondition),
    listTable.columnWhere
  );
  const employeeFilter = mergeWhere(
    {
      ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
      ...(filters.keyword.trim()
        ? {
            $or: [
              { DisplayName: containsFilter(filters.keyword) },
              { Email: containsFilter(filters.keyword) },
            ],
          }
        : {}),
    },
    rosterTable.columnWhere
  );

  const list = useGetAttendances(
    {
      pageNo: listTable.pagination.pageIndex + 1,
      pageSize: listTable.pagination.pageSize,
      filter: recordFilter,
      order: listTable.order,
    },
    !isRoster
  );
  const roster = useGetAttendanceRoster(
    {
      day: filters.date,
      employeeFilter,
      pageNo: rosterTable.pagination.pageIndex + 1,
      pageSize: rosterTable.pagination.pageSize,
      order: rosterTable.order,
    },
    isRoster
  );
  const listResult = (list.data as { Attendances?: AttendancesPagingResult })?.Attendances;
  // Records carry no email; roster rows get it with the employee.
  const { byUserId } = useEmployeesByUserIds(
    isRoster ? [] : (listResult?.items ?? []).map((record) => record.UserId)
  );
  const active = isRoster ? roster : list;

  // The export follows what the table shows: the same filters, column filters and order.
  const handleExport = async (format: 'csv' | 'pdf') => {
    const rows = isRoster
      ? exportRowsOfRoster(
          await fetchAllPages(
            async (pageNo, pageSize) => {
              const page = await getAttendanceRoster({
                day: filters.date,
                employeeFilter,
                pageNo,
                pageSize,
                order: rosterTable.order,
              });
              return { items: page.rows, totalCount: page.totalCount };
            },
            EXPORT_PAGE_SIZE,
            EXPORT_MAX_ROWS
          ),
          filters.date
        )
      : exportRowsOfRecords(
          await fetchAllPages(
            async (pageNo, pageSize) => {
              const page = await getAttendances({
                pageNo,
                pageSize,
                filter: recordFilter,
                order: listTable.order,
              });
              return { items: page.Attendances.items, totalCount: page.Attendances.totalCount };
            },
            EXPORT_PAGE_SIZE,
            EXPORT_MAX_ROWS
          )
        );
    const scope = filters.date || 'all-days';
    if (format === 'csv') downloadAttendanceCsv(rows, t, scope);
    else downloadAttendancePdf(rows, t, scope, `${t('MANAGE_ATTENDANCE')} ${filters.date}`);
  };

  const table = isRoster ? (
    <AdvanceDataTable
      {...rosterTable.tableProps}
      data={roster.data?.rows ?? []}
      columns={createRosterTableColumns({ t, showBreak, departments })}
      onRowClick={(row: RosterRow) => navigate(appPath.teamAttendanceOf(row.employee.UserId))}
      isLoading={roster.isLoading}
      error={roster.error instanceof Error ? roster.error : null}
      pagination={{
        pageIndex: rosterTable.pagination.pageIndex,
        pageSize: rosterTable.pagination.pageSize,
        totalCount: roster.data?.totalCount ?? 0,
      }}
      isExpandRowContent={false}
      mobileView="cards"
      mobileFilterSlot={filterSlot}
    />
  ) : (
    <AdvanceDataTable
      {...listTable.tableProps}
      data={listResult?.items ?? []}
      columns={createAttendanceTableColumns({
        t,
        emailOf: (userId) => byUserId.get(userId)?.Email,
        showBreak,
        departments,
      })}
      onRowClick={(record: AttendanceRecord) => navigate(appPath.teamAttendanceOf(record.UserId))}
      isLoading={list.isLoading}
      error={list.error instanceof Error ? list.error : null}
      pagination={{
        pageIndex: listTable.pagination.pageIndex,
        pageSize: listTable.pagination.pageSize,
        totalCount: listResult?.totalCount ?? 0,
      }}
      isExpandRowContent={false}
      mobileView="cards"
      mobileFilterSlot={filterSlot}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {/* On a phone the day picker has its own row, with Import, CSV and PDF sharing the row under it. */}
      <div className="flex flex-wrap items-end justify-between gap-2 max-md:flex-col max-md:items-stretch">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="min-w-0 flex-1">
            <ManageAttendanceFilters
              value={filters}
              onChange={handleFiltersChange}
              showDate
              maxDate={today}
            />
          </div>
          {/* On a phone the cards' filter button sits here, beside the day picker. */}
          <span ref={setFilterSlot} className="contents md:hidden" />
        </div>
        <div className="flex gap-2 max-md:w-full max-md:[&>div]:contents max-md:[&_button]:flex-1">
          {canImport && (
            <Button
              variant="outline"
              title={t('IMPORT')}
              aria-label={t('IMPORT')}
              onClick={() => setIsImportOpen(true)}
            >
              <Upload className="size-4" />
              {t('IMPORT')}
            </Button>
          )}
          {canExport &&
            (active.isLoading ? (
              // Hold the buttons' place while the list loads (they export what it holds).
              <div className="flex gap-2">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-20" />
              </div>
            ) : (
              <AttendanceExportButtons onExport={handleExport} />
            ))}
        </div>
      </div>
      {table}
      <ImportAttendanceDialog open={isImportOpen} onOpenChange={setIsImportOpen} />
    </div>
  );
};

const CorrectionRequestsTab = () => {
  const { t } = useTranslation();
  const canReview = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_REVIEW_CORRECTION);
  const canWaive = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_WAIVE_CORRECTION_FINE);
  const reviewer = useCurrentReviewer();

  // A notification's link lists only its correction.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_ATTENDANCE_CORRECTIONS_ORDER,
    linkedId,
  });
  // The decision picked from a row's menu, waiting for its confirmation.
  const [reviewTarget, setReviewTarget] = useState<{
    correction: AttendanceCorrection;
    action: 'Approved' | 'Rejected';
  } | null>(null);
  const isRejecting = reviewTarget?.action === 'Rejected';
  const [rejectionNote, setRejectionNote] = useState('');
  const { mutateAsync: reviewCorrection } = useReviewAttendanceCorrection();
  const [waiveTarget, setWaiveTarget] = useState<AttendanceCorrection | null>(null);
  const [bulk, setBulk] = useState<{
    itemIds: string[];
    action: 'Approved' | 'Rejected';
    reset?: () => void;
  }>({
    itemIds: [],
    action: 'Approved',
  });
  const { mutateAsync: waiveFine } = useWaiveCorrectionFine();

  // Every filter is a column filter (status, department, dates); this keeps the reviewer's
  // department scope.
  const filters: ManageAttendanceFiltersValue = EMPTY_MANAGE_ATTENDANCE_FILTERS;
  const { departments, getDepartmentIdCondition } = useDepartmentScope();

  // Corrections store no name: the keyword is matched through the employees it finds, and a keyword
  // that finds nobody lists nothing rather than everything.
  const keyword = filters.keyword.trim();
  const keywordSearch = useSearchCorrectionEmployees(keyword, filters.departmentId || undefined);
  const keywordUserIds = keyword
    ? keywordSearch.data?.map((employee) => employee.UserId)
    : undefined;
  const isKeywordEmpty = keywordUserIds?.length === 0;
  // Pending first; a notification's link opens All, so a decided correction still shows.
  const [queue, setQueue] = useQueueStatus(CORRECTION_QUEUES, 'Pending', 'All');
  const scopeFilter = buildCorrectionFilter(
    filters,
    getDepartmentIdCondition(filters.departmentId),
    keywordUserIds
  );
  const queueCounts = useCorrectionQueueCounts(scopeFilter);
  const { data, isLoading, error } = useGetAttendanceCorrections(
    {
      pageNo: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filter: mergeWhere({ ...scopeFilter, ...correctionQueueFilter(queue) }, columnWhere),
      order,
    },
    (!keyword || Boolean(keywordUserIds)) && !isKeywordEmpty
  );
  const result = isKeywordEmpty
    ? undefined
    : (data as { AttendanceCorrections?: AttendanceCorrectionsPagingResult })
        ?.AttendanceCorrections;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  const { byUserId } = useEmployeesByUserIds(
    items.flatMap((correction) => [correction.UserId, correction.ActionTakenBy ?? ''])
  );
  const nameOf = (userId: string) => byUserId.get(userId)?.DisplayName;

  const columns = createCorrectionRequestsTableColumns({
    t,
    canApprove: canReview || canWaive,
    reviewerId: reviewer.itemId,
    selectable: canReview,
    onReview: (correction, action) => {
      setRejectionNote('');
      setReviewTarget({ correction, action });
    },
    onWaiveFine: canWaive ? setWaiveTarget : undefined,
    nameOf,
    emailOf: (userId) => byUserId.get(userId)?.Email,
    departments,
  });

  return (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        getRowId={(row) => row.ItemId}
        canSelectRow={(row) => isReviewable(row, reviewer.itemId)}
        clearSelectionOnPageChange
        columnsToolbar={(table) => {
          const selected = canReview
            ? table
                .getSelectedRowModel()
                .rows.map((row) => row.original)
                .filter((correction) => isReviewable(correction, reviewer.itemId))
            : [];
          // The toolbar holds the queue switch; while rows are selected it becomes the bulk bar.
          if (selected.length === 0)
            return (
              <QueueStatusFilter
                value={queue}
                onChange={setQueue}
                options={CORRECTION_QUEUES.map((value) => ({
                  value,
                  label: QUEUE_LABEL[value],
                  count: queueCounts[value],
                }))}
              />
            );
          const open = (action: 'Approved' | 'Rejected') =>
            setBulk({
              itemIds: selected.map((c) => c.ItemId),
              action,
              reset: () => table.resetRowSelection(),
            });
          return (
            <BulkSelectionBar
              table={table}
              count={selected.length}
              onApprove={() => open('Approved')}
              onReject={() => open('Rejected')}
            />
          );
        }}
        isLoading={isLoading || keywordSearch.isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <ConfirmationModal
        open={Boolean(reviewTarget)}
        onOpenChange={(open) => !open && setReviewTarget(null)}
        title={t(isRejecting ? 'REJECT_CORRECTION_REQUEST' : 'APPROVE_CORRECTION_REQUEST')}
        description={t(
          isRejecting ? 'REJECT_CORRECTION_CONFIRMATION' : 'APPROVE_CORRECTION_CONFIRMATION'
        )}
        confirmText={isRejecting ? 'REJECT' : 'APPROVE'}
        confirmClassName={isRejecting ? 'destructive' : undefined}
        // The reject workflow requires a reason; it goes to the employee.
        confirmDisabled={isRejecting && !rejectionNote.trim()}
        onConfirm={() =>
          reviewTarget &&
          reviewCorrection({
            itemId: reviewTarget.correction.ItemId,
            status: reviewTarget.action,
            ...(isRejecting ? { note: rejectionNote.trim() } : {}),
          })
        }
      >
        {isRejecting && (
          <div className="space-y-1.5">
            <Label htmlFor="rejection-note">{t('REJECTION_NOTE')}</Label>
            <Textarea
              id="rejection-note"
              rows={3}
              placeholder={t('REJECTION_NOTE')}
              value={rejectionNote}
              onChange={(event) => setRejectionNote(event.target.value)}
            />
          </div>
        )}
      </ConfirmationModal>

      <BulkReviewDialog
        itemIds={bulk.itemIds}
        action={bulk.action}
        onClose={(reviewed) => {
          if (reviewed) bulk.reset?.();
          setBulk((prev) => ({ ...prev, itemIds: [] }));
        }}
      />

      <ConfirmationModal
        open={Boolean(waiveTarget)}
        onOpenChange={(open) => !open && setWaiveTarget(null)}
        title={t('WAIVE_FINE')}
        description={t('WAIVE_FINE_CONFIRMATION', { amount: (waiveTarget?.Fine ?? 0).toFixed(2) })}
        confirmText="WAIVE_FINE"
        onConfirm={() => waiveTarget && waiveFine(waiveTarget.ItemId)}
      />
    </div>
  );
};

const { EMPLOYEES, CORRECTIONS, OVERTIME } = TEAM_ATTENDANCE_TABS;
const QUEUE_LABEL: Record<CorrectionQueue, string> = {
  Pending: 'PENDING',
  Approved: 'APPROVED',
  Rejected: 'REJECTED',
  All: 'ALL',
};
/** Links sent before the tabs were renamed (`?tab=attendance`). */
const TAB_ALIASES: Record<string, TeamAttendanceTab> = { attendance: EMPLOYEES };

/**
 * Team Attendance (HR Module Navigation Map): Employees and Corrections for whoever may see everyone's
 * attendance, Overtime (the former Overtime Manager) for whoever may see everyone's overtime. The tab
 * lives in the URL (`?tab=`); a notification about a correction opens that tab.
 */
export const ManageAttendancePage = () => {
  const { t } = useTranslation();
  const canViewAttendance = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_VIEW_ALL);
  const canViewOvertime = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.OVERTIME_VIEW);
  const tabs: TeamAttendanceTab[] = [
    ...(canViewAttendance ? [EMPLOYEES, CORRECTIONS] : []),
    ...(canViewOvertime ? [OVERTIME] : []),
  ];
  const [tab, setTab] = useUrlTab(tabs, tabs[0] ?? EMPLOYEES, TAB_ALIASES);
  // Corrections waiting for review, on the tab (the same query as the tab's Pending queue).
  const { getDepartmentIdCondition } = useDepartmentScope();
  const pendingCorrections = useCorrectionQueueCounts(
    buildCorrectionFilter(EMPTY_MANAGE_ATTENDANCE_FILTERS, getDepartmentIdCondition(''), undefined),
    canViewAttendance
  ).Pending;

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('TEAM_ATTENDANCE')} />

      <Tabs value={tab} onValueChange={setTab}>
        {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          {canViewAttendance && (
            <>
              <TabsTrigger value={EMPLOYEES} className="max-sm:flex-1">
                {t('EMPLOYEES_TAB')}
              </TabsTrigger>
              <TabsTrigger value={CORRECTIONS} className="max-sm:flex-1" count={pendingCorrections}>
                {t('CORRECTIONS_TAB')}
              </TabsTrigger>
            </>
          )}
          {canViewOvertime && (
            <TabsTrigger value={OVERTIME} className="max-sm:flex-1">
              {t('OVERTIME')}
            </TabsTrigger>
          )}
        </TabsList>
        {canViewAttendance && (
          <>
            <TabsContent value={EMPLOYEES}>
              <AttendanceTab />
            </TabsContent>
            <TabsContent value={CORRECTIONS}>
              <CorrectionRequestsTab />
            </TabsContent>
          </>
        )}
        {canViewOvertime && (
          <TabsContent value={OVERTIME}>
            <OvertimeList
              employeePath={(userId) =>
                appPath.teamAttendanceOf(userId, EMPLOYEE_ATTENDANCE_TABS.OVERTIME)
              }
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
